/**
 * Analytics page – usage metrics, charts (css-only), top users.
 */

import React, { useEffect, useState } from 'react';
import { getAnalytics } from '../services';
import { AnalyticsData } from '../types';
import { BarChart3, Clock, AlertTriangle, Coins, RefreshCw, Wallet } from 'lucide-react';

type Period = '7d' | '30d' | '90d';

export default function AnalyticsPage() {
  const [period, setPeriod] = useState<Period>('30d');
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadAnalytics = async (nextPeriod: Period, isManualRefresh: boolean = false) => {
    if (isManualRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const analytics = await getAnalytics(nextPeriod);
      setData(analytics);
    } finally {
      if (isManualRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    void loadAnalytics(period);
  }, [period]);

  if (loading || !data) return <div className="page-loader">Loading analytics…</div>;

  const buckets = summarizeSeries(data.requestsOverTime ?? []);
  const maxReq = Math.max(...buckets.map((p) => p.value), 1);
  const yTicks = [maxReq, Math.round(maxReq * 0.75), Math.round(maxReq * 0.5), Math.round(maxReq * 0.25), 0];

  return (
    <div className="analytics-page">
      <div className="page-header">
        <div>
          <h1>Analytics</h1>
          <p>Usage metrics for the last {period === '7d' ? '7 days' : period === '30d' ? '30 days' : '90 days'}</p>
        </div>
        <div className="period-selector">
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => void loadAnalytics(period, true)}
            disabled={refreshing}
            title="Refresh analytics"
          >
            <RefreshCw size={14} /> {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
          {(['7d', '30d', '90d'] as Period[]).map((p) => (
            <button key={p} className={`btn btn-sm ${p === period ? 'btn-accent' : 'btn-ghost'}`} onClick={() => setPeriod(p)}>
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* ── Summary cards ─────────────────────────────────── */}
      <div className="metrics-grid cols-4">
        <SummaryCard icon={<BarChart3 size={20} />} label="Total Requests" value={fmt(data.totalRequests ?? 0)} />
        <SummaryCard icon={<Coins size={20} />} label="User Tokens Cost" value={fmt(data.totalTokens ?? 0)} />
        <SummaryCard icon={<Wallet size={20} />} label="Developer Tokens Used" value={fmt(data.developerUserTokenUsed ?? 0)} />
        <SummaryCard icon={<Clock size={20} />} label="Avg Latency" value={`${data.averageLatency ?? 0} ms`} />
      </div>

      {/* ── Requests Over Time ─────────────────────────── */}
      <div className="chart-card">
        <h2>Requests Over Time</h2>
        <div className="bar-chart-wrap">
          <div className="bar-y-axis">
            {yTicks.map((t) => (
              <span key={t} className="bar-y-tick">{fmt(t)}</span>
            ))}
          </div>
          <div className="bar-chart-area">
            <div className="bar-y-gridlines">
              {yTicks.slice(0, -1).map((_, i) => (
                <div key={i} className="bar-y-gridline" />
              ))}
            </div>
            <div className="bar-chart">
              {buckets.map((p, i) => (
                <div key={i} className="bar-col">
                  <div className="bar" style={{ height: `${(p.value / maxReq) * 100}%` }} />
                  <span className="bar-label">{p.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Model usage + errors side by side ─────────── */}
      <div className="analytics-row">
        <div className="chart-card flex1">
          <h2>Usage by Model</h2>
          <div className="model-bars">
            {(data.modelUsage ?? []).map((m) => {
              const maxTokens = Math.max(...(data.modelUsage ?? []).map((x) => x.tokens), 1);
              return (
                <div key={m.model} className="model-bar-row">
                  <span className="model-name">{m.model}</span>
                  <div className="model-bar-track">
                    <div className="model-bar-fill" style={{ width: `${(m.tokens / maxTokens) * 100}%` }} />
                  </div>
                  <span className="model-count">{fmt(m.requests)} req</span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="chart-card flex1">
          <h2>Error Breakdown</h2>
          {(data.errorBreakdown ?? []).length === 0 ? (
            <p className="muted">No errors in this period.</p>
          ) : (
            <div className="error-list">
              {(data.errorBreakdown ?? []).map((e) => (
                <div key={e.type} className="error-row">
                  <AlertTriangle size={14} />
                  <span className="error-type">{e.type}</span>
                  <span className="error-count">{e.count}</span>
                  <span className="error-pct">{e.percentage.toFixed(1)}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Top Users ─────────────────────────────────── */}
      <div className="chart-card">
        <h2>Top Users</h2>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>User ID</th>
                <th>Requests</th>
                <th>Tokens</th>
                <th>Last Active</th>
              </tr>
            </thead>
            <tbody>
              {(data.topUsers ?? []).map((u) => (
                <tr key={u.userId}>
                  <td className="cell-primary"><code>{u.userId}</code></td>
                  <td>{fmt(u.requests)}</td>
                  <td>{fmt(u.tokens)}</td>
                  <td>{new Date(u.lastActive).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ── helpers ──────────────────────────────────────────── */

function fmt(n: number): string {
  return n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000
    ? `${(n / 1_000).toFixed(1)}K`
    : n.toString();
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

interface Bucket {
  label: string;
  value: number;
}

/** Summarise a daily time series into ≤ 10 equal-width buckets. */
function summarizeSeries(points: { date: string; value: number }[]): Bucket[] {
  if (points.length <= 10) {
    return points.map((p) => ({ label: shortDate(p.date), value: p.value }));
  }
  const bucketCount = Math.min(10, points.length);
  const size = Math.ceil(points.length / bucketCount);
  const result: Bucket[] = [];
  for (let i = 0; i < points.length; i += size) {
    const chunk = points.slice(i, i + size);
    const total = chunk.reduce((s, p) => s + p.value, 0);
    const startLabel = shortDate(chunk[0].date);
    const endLabel = shortDate(chunk[chunk.length - 1].date);
    result.push({
      label: chunk.length === 1 ? startLabel : `${startLabel}–${endLabel}`,
      value: total,
    });
  }
  return result;
}

function SummaryCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="metric-card">
      <div className="metric-icon">{icon}</div>
      <div className="metric-body">
        <span className="metric-value">{value}</span>
        <span className="metric-label">{label}</span>
      </div>
    </div>
  );
}
